import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/sales-orders")({
  component: SalesOrdersPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { LocalSearchSelect } from "@/components/reusable-components/local-search-select";
import { AccountSearchSelect } from "@/components/crm/shared/AccountSearchSelect";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { todayInOrgTimezone } from "@/lib/date-utils";
import {
  useFetchSalesOrders,
  useSalesOrderMutations,
  type SalesOrderOrdering,
} from "@/hooks/api/inventory/useSales";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useFetchWarehouses,
  useFetchItems,
  useFetchUnits,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { SalesFormSheet } from "@/components/inventory/forms/SalesFormSheet";
import { SalesOrderTable } from "@/components/inventory/tables/SalesOrderTable";
import { SalesOrderDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type {
  SalesOrder,
  SalesOrderPayload,
  SalesOrderStatus,
} from "@/types/inventory";

const STATUS_OPTIONS = [
  { label: "Draft", value: "DRAFT" },
  { label: "Confirmed", value: "CONFIRMED" },
  { label: "Shipped", value: "SHIPPED" },
  { label: "Delivered", value: "DELIVERED" },
  { label: "Cancelled", value: "CANCELLED" },
] satisfies { label: string; value: SalesOrderStatus }[];

function SalesOrdersPage() {
  const navigate = useNavigate();
  const {
    createSalesOrder,
    updateSalesOrder,
    confirmSalesOrder,
    cancelSalesOrder,
    generateDeliveries,
  } = useSalesOrderMutations();
  const isMutating =
    createSalesOrder.isPending ||
    updateSalesOrder.isPending ||
    confirmSalesOrder.isPending ||
    cancelSalesOrder.isPending;
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [statusFilter, setStatusFilter] = useState<
    SalesOrderStatus | undefined
  >(undefined);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [accountFilter, setAccountFilter] = useState<string | undefined>(
    undefined
  );
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [showFilters, setShowFilters] = useState(false);
  const [viewTarget, setViewTarget] = useState<SalesOrder | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<SalesOrder | null>(null);
  const [cancelTarget, setCancelTarget] = useState<SalesOrder | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<SalesOrder | null>(null);
  const [generateDeliveriesTarget, setGenerateDeliveriesTarget] =
    useState<SalesOrder | null>(null);

  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS, {
    enabled: isSheetOpen,
  });
  const items = itemsQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS, {
    enabled: isSheetOpen,
  });
  const units = unitsQuery.data?.results ?? [];

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        so_number: SortDirection;
        status: SortDirection;
        order_date: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      SalesOrderOrdering
    >({
      so_number: null,
      status: null,
      order_date: null,
      created_at: null,
      updated_at: null,
    });

  const salesOrdersQuery = useFetchSalesOrders({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    status: statusFilter,
    warehouse: warehouseFilter ? Number(warehouseFilter) : undefined,
    account: accountFilter ? Number(accountFilter) : undefined,
  });
  const salesOrders = salesOrdersQuery.data?.results ?? [];
  const count = salesOrdersQuery.data?.count ?? 0;

  const isLoading = salesOrdersQuery.isPending || isMutating;
  const isFetching = salesOrdersQuery.isFetching || isMutating;

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    ordering,
    statusFilter,
    warehouseFilter,
    accountFilter,
    setPage,
  ]);

  const resetFilters = () => {
    setOrdering({
      so_number: null,
      status: null,
      order_date: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setWarehouseFilter(undefined);
    setAccountFilter(undefined);
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
  };

  const handleOpenSheet = () => {
    setIsSheetOpen(true);
  };

  const handleSubmit = async (payload: SalesOrderPayload) => {
    if (editing) {
      await updateSalesOrder.mutateAsync({ id: editing.id, payload });
      toast.success("Sales order updated");
    } else {
      await createSalesOrder.mutateAsync(payload);
      toast.success("Sales order created");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleConfirmOrder = async () => {
    if (!confirmTarget) return;
    try {
      await confirmSalesOrder.mutateAsync({ id: confirmTarget.id });
      toast.success("Sales order confirmed");
      setConfirmTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await cancelSalesOrder.mutateAsync({ id: cancelTarget.id });
      toast.success("Sales order cancelled");
      setCancelTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleGenerateDeliveries = async () => {
    if (!generateDeliveriesTarget) return;
    try {
      const result = await generateDeliveries.mutateAsync({
        id: generateDeliveriesTarget.id,
        delivery_date: todayInOrgTimezone(),
      });
      const count = result.delivery_orders.length;
      if (count === 0) {
        toast.error("Nothing left to deliver for this sales order.");
      } else {
        toast.success(
          `Created ${count} delivery order${count === 1 ? "" : "s"}` +
            (result.skipped_serialized_items.length
              ? ` — ${result.skipped_serialized_items.join(", ")} need${result.skipped_serialized_items.length === 1 ? "s" : ""} manual asset selection`
              : "")
        );
      }
      setGenerateDeliveriesTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Sales Orders"
          description="Manage outbound sales orders"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={handleOpenSheet}
          addLabel="New Sales Order"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search order no..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
          <LocalSearchSelect
            className="w-fit"
            items={warehouses}
            keyExtractor={(w) => w.id}
            labelExtractor={(w) => w.name}
            watchedValue={warehouseFilter ?? null}
            onSelect={(v) => setWarehouseFilter(v == null ? undefined : String(v))}
            placeholder="Warehouse"
            searchPlaceholder="Search warehouses..."
            noItemsMessage="No warehouses found."
          />
          <AccountSearchSelect
            className="w-fit"
            value={accountFilter ?? null}
            onChange={(v) => setAccountFilter(v == null ? undefined : String(v))}
            placeholder="Account"
          />
        </FiltersPanel>

        <SalesOrderTable
          salesOrders={salesOrders}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onNameClick={(so) =>
            navigate({
              to: "/admin/inventory/sales-orders/$id",
              params: { id: String(so.id) },
            })
          }
          onView={(so) => setViewTarget(so)}
          onEdit={(so) => {
            setEditing(so);
            handleOpenSheet();
          }}
          onConfirm={(so) => setConfirmTarget(so)}
          onCancel={(so) => setCancelTarget(so)}
          onGenerateDeliveries={(so) => setGenerateDeliveriesTarget(so)}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <SalesFormSheet
          mode="so"
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          warehouses={warehouses}
          items={items}
          units={units}
          isLoading={isMutating}
          editing={editing}
        />

        <DeleteConfirmDialog
          isOpen={!!confirmTarget}
          onClose={() => setConfirmTarget(null)}
          onConfirm={handleConfirmOrder}
          title="Confirm Sales Order"
          description={`Confirm sales order ${confirmTarget?.so_number}? This will allow creating Delivery Orders.`}
          itemName={confirmTarget?.so_number}
          isLoading={isMutating}
        />

        <DeleteConfirmDialog
          isOpen={!!cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleCancelConfirm}
          title="Cancel Sales Order"
          description={`Cancel sales order ${cancelTarget?.so_number}? This cannot be undone.`}
          itemName={cancelTarget?.so_number}
          isLoading={isMutating}
          destructive
        />

        <DeleteConfirmDialog
          isOpen={!!generateDeliveriesTarget}
          onClose={() => setGenerateDeliveriesTarget(null)}
          onConfirm={handleGenerateDeliveries}
          title="Generate Deliveries"
          description={`Splits sales order ${generateDeliveriesTarget?.so_number}'s remaining items into one Delivery Order per warehouse (today's date). Serialized items are skipped -- deliver those manually so specific units can be picked.`}
          itemName={generateDeliveriesTarget?.so_number}
          isLoading={generateDeliveries.isPending}
          destructive={false}
          confirmLabel="Generate"
          loadingLabel="Generating..."
        />
      </div>

      <SalesOrderDetailSheet
        isOpen={!!viewTarget}
        onClose={() => setViewTarget(null)}
        so={viewTarget}
      />
    </SidebarInset>
  );
}
